import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import "./header.css";
// import HeaderLogoCarousel from "./HeaderLogoCarousel";

// Static five-logo strip, in the order Jacob asked for on 2026-09-10:
// Penn, Hamilton, Denver, Jefferson, Lander. Not alphabetical — leave as is.
//
// These point at assets/Schools/trimmed, not the originals. Logo files arrive
// with wildly different amounts of empty space baked around the mark — Denver's
// filled 40% of its frame's height, Lander's 65% — so sizing them with one CSS
// rule rendered them at completely different sizes. The trimmed copies are
// cropped to the ink, which lets a single max-height in header.css land every
// mark at the same size, the way the career centre row does. Trim any new logo
// the same way before adding it here, rather than scaling it in code.
//
// Use logo files with transparent backgrounds — an opaque one shows as a
// box over the bright parts of the hero.
import logoPenn from "../../../assets/Schools/trimmed/University of Pennsylvania.webp";
import logoHamilton from "../../../assets/Schools/trimmed/Hamilton College.webp";
import logoDenver from "../../../assets/Schools/trimmed/University of Denver.webp";
import logoJefferson from "../../../assets/Schools/trimmed/Jefferson Community College.webp";
import logoLander from "../../../assets/Schools/trimmed/Lander University.webp";

// `h` is each mark's height as a multiple of the row's base height, not a
// pixel value, so the whole row scales together at smaller breakpoints.
//
// These are matched on ink mass rather than bounding box. Sizing every logo to
// the same height looks wrong, because a dense wide lockup like Denver carries
// far more weight at 42px than a sparse stacked one like Jefferson. Each value
// below is the height at which that mark renders about as much ink as Penn, so
// they read as one size. Measured, then eased by eye: Denver is nudged up a
// little, since a solid block reads as one shape rather than as extra mass.
const featuredSchools = [
  { name: "University of Pennsylvania", src: logoPenn, h: 1 },
  { name: "Hamilton College", src: logoHamilton, h: 1.67 },
  { name: "University of Denver", src: logoDenver, h: 0.81 },
  { name: "Jefferson Community College", src: logoJefferson, h: 1.71 },
  { name: "Lander University", src: logoLander, h: 1.05 },
];

/* How long the tubes travel on their own before the cursor takes over —
   counted from when they are actually up, not when they start fading in.
   On a first visit the intro fades the canvas in over 1.3s, so counting from
   the first non-zero opacity spent most of the run behind that fade. */
const AUTOPLAY_MS = 2000;
const VISIBLE_AT = 0.9;

/* A canvas that never reaches full opacity must not lock the cursor out, so
   the handover happens regardless once this much time has passed. */
const HANDOVER_CAP_MS = 12000;

const randomColors = (count) =>
  Array.from({ length: count }, () =>
    "#" + Math.floor(Math.random() * 16777215).toString(16).padStart(6, "0")
  );

/* Booking lives in one place — the form at the foot of the career centre
   page, the same destination the "Book a walkthrough" link at the bottom of
   the home page uses. ScrollToTop resolves the hash, so this lands on the
   form rather than the top of that page. */
const BOOK_URL = "/career-centers#book";

const Header = ({
  heading = "AI For The Job Search",
  subtext = "",
  ctaLabel = "Sign Up",
  bookLabel = "",
  logoLabel = "Trusted by students at 1,000+ schools",
  showLogoLabel = false,
  wrapHeading = false,
}) => {
  const canvasRef = useRef(null);
  const tubesRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    let rafId = null;
    let removeTouch = null;

    const init = async () => {
      if (!canvasRef.current) return;
      try {
        /* Hold the library's own mouse handlers back so the tubes can be
           driven along their own path first, then hand them over.

           This patches EventTarget.prototype rather than the three obvious
           targets: the library binds its listener to an element it creates
           itself, so patching window/document/canvas caught nothing and the
           cursor was driving the whole time. The patch is also kept in place
           through construction — restoring it before TubesCursor() ran was
           the other half of that bug.

           The prototype is global, so the window is kept as small as
           possible: it opens just before the import and closes the moment the
           instance exists. Anything else that happens to register a
           mousemove inside it is replayed at handover rather than lost. */
        const held = [];
        const origAdd = EventTarget.prototype.addEventListener;
        EventTarget.prototype.addEventListener = function (type, fn, opts) {
          if (type === "mousemove" || type === "pointermove") {
            held.push({ target: this, type, fn, opts });
            return undefined;
          }
          return origAdd.call(this, type, fn, opts);
        };

        let app;
        try {
          // webpackIgnore tells webpack to leave this URL alone — it resolves natively in the browser
          const mod = await import(
            /* webpackIgnore: true */
            "https://cdn.jsdelivr.net/npm/threejs-components@0.0.19/build/cursors/tubes1.min.js"
          );

          if (!mounted || !canvasRef.current) {
            EventTarget.prototype.addEventListener = origAdd;
            held.forEach(({ target, type, fn, opts }) => origAdd.call(target, type, fn, opts));
            return;
          }

          const TubesCursor = mod.default;
          app = TubesCursor(canvasRef.current, {
            tubes: {
              colors: ["#5e72e4", "#8965e0", "#f5365c"],
              lights: {
                intensity: 200,
                colors: ["#21d4fd", "#b721ff", "#f4d03f", "#11cdef"],
              },
            },
          });
        } finally {
          EventTarget.prototype.addEventListener = origAdd;
        }

        tubesRef.current = app;

        /* The library follows the cursor through a pointermove on
           document.body. On a phone that stream dies the moment a drag turns
           into a scroll — the browser sends pointercancel and stops — so the
           tubes never followed a finger. touchmove keeps firing through a
           scroll, so touches are fed to the same handlers directly. The
           listeners are passive and the canvas keeps touch-action: auto below
           1024px, so the page still scrolls under the finger. */
        const followers = held
          .filter(({ target, type }) => target === document.body && type === "pointermove")
          .map(({ fn }) => fn);
        let handedOver = false;

        const handOver = () => {
          handedOver = true;
          held.forEach(({ target, type, fn, opts }) => origAdd.call(target, type, fn, opts));
          held.length = 0;
        };

        const onTouch = (e) => {
          const touch = e.touches[0];
          if (!touch) return;
          // A touch during the autoplay ends it early rather than fighting it.
          if (!handedOver) handOver();
          const synth = new MouseEvent("mousemove", {
            clientX: touch.clientX,
            clientY: touch.clientY,
          });
          followers.forEach((fn) => fn(synth));
        };

        const section = sectionRef.current;
        if (section) {
          section.addEventListener("touchstart", onTouch, { passive: true });
          section.addEventListener("touchmove", onTouch, { passive: true });
          removeTouch = () => {
            section.removeEventListener("touchstart", onTouch);
            section.removeEventListener("touchmove", onTouch);
          };
        }

        /* Timed from when the canvas is actually on screen, not from load: on
           a first visit the intro curtain covers the hero for several seconds,
           and counting from load would spend the whole run behind it. */
        let shownAt = null;
        const startedAt = performance.now();
        let t = -Math.PI / 2;

        const drive = () => {
          if (!mounted || !canvasRef.current) return;
          const canvas = canvasRef.current;
          const visible = parseFloat(getComputedStyle(canvas).opacity) >= VISIBLE_AT;
          const now = performance.now();

          if (visible && shownAt === null) shownAt = now;

          const played = shownAt !== null && now - shownAt >= AUTOPLAY_MS;
          if (handedOver) return;
          if (played || now - startedAt >= HANDOVER_CAP_MS) {
            handOver();
            return;
          }

          const rect = canvas.getBoundingClientRect();
          const cx = rect.left + rect.width / 2;
          const cy = rect.top + rect.height / 2;

          // A figure-eight, so the tubes sweep rather than circle.
          const x = cx + rect.width * 0.3 * Math.sin(t);
          const y = cy + rect.height * 0.26 * Math.sin(2 * t);
          const synth = new MouseEvent("mousemove", { clientX: x, clientY: y });
          held.forEach(({ fn }) => fn(synth));

          t += 0.008;
          rafId = requestAnimationFrame(drive);
        };

        rafId = requestAnimationFrame(drive);
      } catch (err) {
        console.error("Tubes failed to load:", err);
      }
    };

    // Defer CDN load until after first paint so it doesn't block the hero
    const timer = setTimeout(init, 0);
    return () => {
      mounted = false;
      clearTimeout(timer);
      if (rafId) cancelAnimationFrame(rafId);
      if (removeTouch) removeTouch();
    };
  }, []);

  const handleClick = () => {
    if (!tubesRef.current) return;
    tubesRef.current.tubes.setColors(randomColors(3));
    tubesRef.current.tubes.setLightsColors(randomColors(4));
  };

  return (
    <section className="header" ref={sectionRef} onClick={handleClick}>
      {/* Three.js tubes canvas — covers the full section */}
      <canvas
        ref={canvasRef}
        className="header-canvas"
        style={{ touchAction: "none" }}
      />

      {/* Content overlay */}
      <div
        className={`header-content${subtext ? " header-content--with-sub" : ""}`}
      >
        <h1
          className={`header-heading${wrapHeading ? " header-heading--wrap" : ""}`}
        >
          {heading}
        </h1>

        {subtext && <p className="header-sub">{subtext}</p>}

        {/* The section swallows clicks to recolour the tubes, so every
            control in here stops propagation on its way out. */}
        <div className="header-actions">
          <button
            className="header-cta"
            onClick={(e) => {
              e.stopPropagation();
              window.location.href = "https://app.raincareers.com/auth?mode=signUp";
            }}
          >
            {ctaLabel}
          </button>

          {bookLabel && (
            <Link
              className="header-cta header-cta--link"
              to={BOOK_URL}
              onClick={(e) => e.stopPropagation()}
            >
              {bookLabel}
              <ChevronRight size={18} strokeWidth={2.5} />
            </Link>
          )}
        </div>
      </div>

      {/* Logo strip */}
      <div className="header-logo-strip">
        {showLogoLabel && <p className="header-logo-label">{logoLabel}</p>}
        {/* The rotating carousel that used to sit under the label is still
            parked; the static five-logo row below replaced it.
        <HeaderLogoCarousel />
        */}
        <div className="header-logo-row">
          {featuredSchools.map(({ name, src, h }) => (
            <span className="header-logo-item" key={name}>
              <img decoding="async" src={src} alt={name} style={{ "--h": h }} />
            </span>
          ))}
        </div>
      </div>


    </section>
  );
};

export default Header;
